import { getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import {
  initializeFirestore,
  type Firestore,
  setLogLevel,
  persistentLocalCache,
  persistentMultipleTabManager,
  memoryLocalCache,
} from 'firebase/firestore';
import { getAuth, type Auth } from 'firebase/auth';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import { getFunctions, type Functions } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaV3Provider, type AppCheck } from 'firebase/app-check';
import { logger } from '@/lib/logger';
import { getFirebaseConfig } from '@/lib/env/requireEnv';

const env = import.meta.env;

// App Check instance (exported for use in other modules)
let appCheckInstance: AppCheck | null = null;

// Get validated Firebase config - fails fast in staging/prod if vars missing
const config = getFirebaseConfig();

// DEBUG: Verify authDomain source
if (import.meta.env.DEV) {
  logger.info('[Firebase] Init config debug', {
    envAuthDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    configAuthDomain: config.authDomain,
    expected: 'modcar-final.firebaseapp.com',
    match: config.authDomain === 'modcar-final.firebaseapp.com',
  });
}

const app: FirebaseApp = getApps()[0] ?? initializeApp(config);

// Initialize Firestore with offline persistence
// Try multi-tab persistence first, fallback to single-tab, then memory cache
let firestoreDb: Firestore;
try {
  // Multi-tab persistence for better UX when users have multiple tabs open
  firestoreDb = initializeFirestore(app, {
    experimentalAutoDetectLongPolling: true,
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager(),
    }),
  });
  logger.info('[Firebase] Firestore initialized with multi-tab persistence');
} catch (error) {
  logger.warn('[Firebase] Multi-tab persistence failed, trying single-tab:', error);
  try {
    // Fallback to single-tab persistence
    firestoreDb = initializeFirestore(app, {
      experimentalAutoDetectLongPolling: true,
      localCache: persistentLocalCache(),
    });
    logger.info('[Firebase] Firestore initialized with single-tab persistence');
  } catch (fallbackError) {
    logger.warn('[Firebase] Persistence failed, using memory cache:', fallbackError);
    // Final fallback to memory cache (no offline support)
    firestoreDb = initializeFirestore(app, {
      experimentalAutoDetectLongPolling: true,
      localCache: memoryLocalCache(),
    });
    logger.info('[Firebase] Firestore initialized with memory cache (no offline support)');
  }
}

export const db: Firestore = firestoreDb;

// Reduce Firestore logging noise in production
if (import.meta.env.PROD) {
  setLogLevel('error');
}

export const auth: Auth = getAuth(app);

// CONTRACT v2.2.0 §15: Production runtime authDomain enforcement
// Fail-fast if Firebase Auth instance doesn't match expected env config
if (import.meta.env.PROD) {
  const actualAuthDomain = auth.app.options.authDomain;
  const expectedAuthDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;

  if (actualAuthDomain !== expectedAuthDomain) {
    logger.error('[Firebase] CRITICAL: authDomain mismatch detected', {
      actual: actualAuthDomain,
      expected: expectedAuthDomain,
    });
    throw new Error(
      `Production authDomain mismatch: Firebase Auth initialized with "${actualAuthDomain}" ` +
        `but VITE_FIREBASE_AUTH_DOMAIN is "${expectedAuthDomain}". ` +
        `This will cause verification emails to fail. Check your build configuration.`
    );
  }

  logger.info('[Firebase] ✅ authDomain validated', { authDomain: actualAuthDomain });
}

export const storage: FirebaseStorage = getStorage(app);

// Initialize Firebase Functions (europe-west1 region for lower latency in EU)
const FUNCTIONS_REGION = 'europe-west1';
export const functions: Functions = getFunctions(app, FUNCTIONS_REGION);

// Dev-only log to help debug region mismatches
if (import.meta.env.DEV) {
  logger.info(`[Firebase] Functions region: ${FUNCTIONS_REGION}`);
}

logger.info(
  `[Firebase] Project: ${config.projectId} | Storage Bucket: ${config.storageBucket} | Emulators: ${env.VITE_USE_EMULATORS}`
);

// Log storage configuration
if (!config.storageBucket) {
  logger.error(
    '[Firebase] WARNING: Storage bucket is not configured! Storage operations will fail.'
  );
} else {
  logger.info('[Firebase] Storage initialized with bucket:', config.storageBucket);
}

// ============================================================================
// App Check Initialization (reCAPTCHA v3)
// ============================================================================
// Protects Firebase services from abuse by verifying requests come from
// legitimate app instances. Uses invisible reCAPTCHA v3 (no user interaction).
//
// LOCALHOST DEVELOPMENT:
// reCAPTCHA v3 does NOT work reliably on localhost for Cloud Functions calls.
// We MUST use debug tokens for local development:
// 1. Debug mode is AUTO-ENABLED on localhost (no env var needed)
// 2. On first run, copy the debug token from browser console
// 3. Register it in Firebase Console > App Check > Apps > Manage debug tokens
// 4. Refresh the page - App Check will now work
//
// PRODUCTION:
// Uses VITE_RECAPTCHA_V3_SITE_KEY with reCAPTCHA v3 (no debug mode)
// ============================================================================

const recaptchaSiteKey = env.VITE_RECAPTCHA_V3_SITE_KEY || env.VITE_APPCHECK_KEY;

// Detect localhost for automatic debug mode
const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

if (recaptchaSiteKey) {
  try {
    // Debug mode: AUTO-ENABLE on localhost, or explicit opt-in via VITE_ENABLE_APPCHECK_DEBUG=true
    // This is REQUIRED for localhost because reCAPTCHA v3 tokens are rejected by Cloud Functions
    const shouldUseDebugMode = isLocalhost || import.meta.env.VITE_ENABLE_APPCHECK_DEBUG === 'true';

    if (shouldUseDebugMode) {
      // Use explicit debug token from env if provided, otherwise auto-generate
      // Vite requires VITE_ prefix to expose env vars to client
      const debugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;

      // @ts-expect-error - FIREBASE_APPCHECK_DEBUG_TOKEN is a special global
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken || true;

      if (debugToken) {
        logger.info('[Firebase] App Check DEBUG MODE enabled with explicit token from env');
      } else {
        logger.info(
          '[Firebase] App Check DEBUG MODE enabled (localhost detected).\n' +
            '  → Copy the debug token from console below\n' +
            '  → Register it at: Firebase Console > App Check > Apps > (your app) > Manage debug tokens\n' +
            '  → Add VITE_APPCHECK_DEBUG_TOKEN=<token> to .env.local to skip this step\n' +
            '  → Then refresh this page'
        );
      }
    }

    appCheckInstance = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
    logger.info('[Firebase] App Check initialized', {
      mode: shouldUseDebugMode ? 'DEBUG' : 'PRODUCTION',
      isLocalhost,
    });
  } catch (error) {
    logger.error('[Firebase] Failed to initialize App Check:', error);
  }
} else if (env.PROD) {
  logger.warn('[Firebase] App Check not configured - set VITE_RECAPTCHA_V3_SITE_KEY');
}

// Export App Check instance for modules that need it
export const appCheck = appCheckInstance;

// IMPORTANT: Only connect to emulator when explicitly enabled
if (env.VITE_USE_EMULATORS === 'true') {
  const { connectFirestoreEmulator } = await import('firebase/firestore');
  const { connectStorageEmulator } = await import('firebase/storage');
  const { connectFunctionsEmulator } = await import('firebase/functions');
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  logger.info('[Firebase] Connected to emulators');
}

export default app;
